//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { useReducer } from 'react';

import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { parseInputDate } from '@components/date-range-picker/dateRangePickerUtils';
import DateString from '@models/DateString';
import { isDefined } from '@utils/filterPredicates';

function getCustomStartDate(
  state: DateRangeSelectionState,
): DateString | undefined {
  if (!state.customStartDate.value) {
    return state.startDate;
  }
  const start = parseInputDate(state.customStartDate.value);
  if (start.isValid) {
    return start.parsed;
  }
  return undefined;
}

function getCustomEndDate(
  state: DateRangeSelectionState,
): DateString | undefined {
  if (!state.customEndDate.value) {
    return state.endDate;
  }
  const end = parseInputDate(state.customEndDate.value);
  if (end.isValid) {
    return end.parsed;
  }
  return undefined;
}

interface DateRangeSelectionState {
  startDate?: DateString;
  endDate?: DateString;
  candidate?: DateString;
  aliasKey?: string;
  customStartDate: {
    value?: string;
    isValid: boolean;
  };
  customEndDate: {
    value?: string;
    isValid: boolean;
  };
  minDate?: DateString;
  maxDate?: DateString;
  maxDateRange?: number;
}

interface IDateRangeSelectionSwapToCustomAction {
  type: 'swapToCustomRange';
}
interface IDateRangeSelectionMouseAction {
  type: 'clickDate' | 'hoverDate' | 'unhoverDate';
  date: DateString;
}

interface ICustomDateInputChangedAction {
  type: 'customStartDateChanged' | 'customEndDateChanged';
  value: string;
}
interface IDateRangeSelectionSetAction {
  type: 'setRange';
  startDate: DateString;
  endDate: DateString;
  aliasKey?: string;
}

type DateRangeSelectionAction =
  | ICustomDateInputChangedAction
  | IDateRangeSelectionSwapToCustomAction
  | IDateRangeSelectionMouseAction
  | IDateRangeSelectionSetAction;

/**
 * Reducer for the selection/highlighting state of the date range picker.
 */
function selectionReducer(
  state: DateRangeSelectionState,
  action: DateRangeSelectionAction,
): DateRangeSelectionState {
  switch (action.type) {
    case 'clickDate': {
      const { startDate, endDate, maxDateRange, maxDate, minDate } = state;
      const { date } = action;
      if (isDefined(startDate) && !isDefined(endDate)) {
        // Only 1 date is selected
        const isValidRange = isDefined(maxDateRange)
          ? Math.abs(DateString.getDayOffset(startDate, date)) <= maxDateRange
          : true;
        const [newStartDate, newEndDate] = (() => {
          if (DateString.getDayOffset(startDate, date) > 0) {
            // candidate is after startDate
            return [startDate, date];
          } else {
            // candidate is before startDate
            return [date, startDate];
          }
        })();
        const isWithinMinMax = isDefined(newStartDate)
          ? DateString.isWithin(newStartDate, {
              minDate,
              maxDate,
            })
          : true;

        return {
          ...state,
          startDate: newStartDate,
          endDate: newEndDate,
          candidate: undefined,
          customStartDate: {
            isValid: isValidRange && isWithinMinMax,
          },
          customEndDate: {
            isValid: isValidRange && isWithinMinMax,
          },
          aliasKey: 'Custom',
        };
      } else {
        // 0 dates or both dates are selected
        // start selection process
        return {
          ...state,
          startDate: date,
          endDate: undefined,
          candidate: undefined,
          customStartDate: { isValid: true },
          customEndDate: { isValid: true },
          aliasKey: 'Custom',
        };
      }
    }
    case 'hoverDate': {
      const { date } = action;
      return {
        ...state,
        candidate: date,
      };
    }
    case 'unhoverDate': {
      const { candidate } = state;
      const { date } = action;
      // Mouse leaves a cell.
      // If it's our current candidate, mark it as no longer the candidate.
      return {
        ...state,
        candidate: candidate === date ? undefined : candidate,
      };
    }
    case 'setRange': {
      const { startDate, endDate, aliasKey } = action;
      return {
        ...state,
        startDate,
        endDate,
        aliasKey,
        customStartDate: { isValid: true },
        customEndDate: { isValid: true },
      };
    }
    case 'swapToCustomRange': {
      return {
        ...state,
        aliasKey: 'Custom',
      };
    }
    case 'customStartDateChanged': {
      const val = parseInputDate(action.value);
      const customEndDate = getCustomEndDate(state);
      const { maxDateRange, endDate } = state;

      const isValidRange =
        isDefined(maxDateRange) && isDefined(endDate) && val.isValid
          ? Math.abs(DateString.getDayOffset(val.parsed, endDate)) <=
            maxDateRange
          : true;

      // If both the custom inputs are valid, set them both as the current range.
      // They could both float invalid for a while before becoming valid.
      // e.g., set a new custom start date that is after the current end date, then set an end date that is valid.
      if (
        val.isValid &&
        customEndDate &&
        DateString.compare(val.parsed, customEndDate) <= 0 &&
        DateString.isWithin(val.parsed, {
          minDate: state.minDate,
          maxDate: state.maxDate,
        })
      ) {
        return {
          ...state,
          startDate: val.parsed,
          endDate: customEndDate,
          customStartDate: { isValid: true, value: action.value },
          customEndDate: {
            ...state.customEndDate,
            isValid:
              isValidRange &&
              DateString.isWithin(customEndDate, {
                minDate: state.minDate,
                maxDate: state.maxDate,
              }),
          },
        };
      }

      return {
        ...state,
        customStartDate: {
          value: action.value,
          isValid:
            val.isValid &&
            !customEndDate &&
            isValidRange &&
            DateString.isWithin(val.parsed, {
              minDate: state.minDate,
              maxDate: state.maxDate,
            }),
        },
      };
    }
    case 'customEndDateChanged': {
      const val = parseInputDate(action.value);
      const customStartDate = getCustomStartDate(state);
      const { maxDateRange, startDate } = state;

      const isValidRange =
        isDefined(maxDateRange) && isDefined(startDate) && val.isValid
          ? Math.abs(DateString.getDayOffset(val.parsed, startDate)) <=
            maxDateRange
          : true;

      // If both the custom inputs are valid, set them both as the current range.
      // They could both float invalid for a while before becoming valid.
      // e.g., set a new custom start date that is after the current end date, then set an end date that is valid.
      if (
        val.isValid &&
        customStartDate &&
        DateString.compare(val.parsed, customStartDate) >= 0 &&
        DateString.isWithin(val.parsed, {
          minDate: state.minDate,
          maxDate: state.maxDate,
        })
      ) {
        return {
          ...state,
          startDate: customStartDate,
          endDate: val.parsed,
          customEndDate: { value: action.value, isValid: true },
          customStartDate: {
            ...state.customStartDate,
            isValid:
              isValidRange &&
              DateString.isWithin(customStartDate, {
                minDate: state.minDate,
                maxDate: state.maxDate,
              }),
          },
        };
      }
      return {
        ...state,
        customEndDate: {
          value: action.value,
          isValid:
            val.isValid &&
            !customStartDate &&
            isValidRange &&
            DateString.isWithin(val.parsed, {
              minDate: state.minDate,
              maxDate: state.maxDate,
            }),
        },
      };
    }
  }
}

/**
 * Instantiate the Date Range Picker selection model (state/reducer) and return state & actions.
 */
export function useDateRangeSelectionModel(
  startDate?: DateString,
  endDate?: DateString,
  aliasKey?: string,
  minDate?: DateString,
  maxDate?: DateString,
  maxDateRange?: number,
) {
  const [selectionState, dispatch] = useReducer(selectionReducer, {
    startDate,
    endDate,
    aliasKey,
    minDate,
    maxDate,
    maxDateRange,
    customStartDate: { isValid: true },
    customEndDate: { isValid: true },
  });

  return {
    ...selectionState,
    dateClicked: (date: DateString) =>
      void dispatch({ type: 'clickDate', date }),
    dateHovered: (date: DateString) =>
      void dispatch({ type: 'hoverDate', date }),
    dateUnhovered: (date: DateString) =>
      void dispatch({ type: 'unhoverDate', date }),
    setRange: (alias: IAliasedDateRange) =>
      void dispatch({
        type: 'setRange',
        ...alias,
        aliasKey: alias.displayName,
      }),
    swapToCustomRange: () => void dispatch({ type: 'swapToCustomRange' }),
    customStartDateInputChanged: (value: string) =>
      void dispatch({ type: 'customStartDateChanged', value }),
    customEndDateInputChanged: (value: string) =>
      void dispatch({ type: 'customEndDateChanged', value }),
  };
}
